import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, Tag, ArrowRight, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/design-system';
import GiftPromoCard from '../components/GiftPromoCard';
import { FREE_SHIPPING_THRESHOLD } from '../utils/shipping';

const Cart = () => {
  const { 
    items, 
    totals, 
    coupon, 
    gifts,
    loading, 
    removeItem, 
    updateQuantity, 
    applyCoupon, 
    removeCoupon 
  } = useCart();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;
    
    setApplyingCoupon(true);
    const success = await applyCoupon(couponCode.trim());
    if (success) {
      setCouponCode('');
    }
    setApplyingCoupon(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  // Calcular total sin IVA (IVA absorbido por la empresa)
  // El regalo de 10ml es obligatorio: sin elegirlo no se puede continuar al checkout
  const giftPending = !!(gifts && gifts.applied && !gifts.complete);

  const getFinalTotal = () => {
    const subtotalAfterDiscount = totals.subtotal - (totals.discount_amount || 0) - (totals.promo_discount || 0);
    return subtotalAfterDiscount;
  };

  if (items.length === 0) {
    return (
      <div className="pb-12 px-4 md:px-12 max-w-7xl mx-auto min-h-screen bg-brand-bone" data-testid="cart-page-empty">
        <div className="text-center py-12 md:py-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/vng9vv17_logo-alphatica-blanco.png"
            alt="Alphatica"
            className="h-12 md:h-16 w-auto mx-auto mb-6 opacity-30"
          />
          <ShoppingBag className="mx-auto h-12 w-12 md:h-16 md:w-16 text-brand-cobalt/30 mb-4" />
          <h1 className="font-display text-2xl md:text-4xl tracking-tight font-light mb-3 text-brand-black">
            Tu carrito está vacío
          </h1>
          <p className="text-brand-black/50 text-sm md:text-base mb-6">
            Explora nuestra colección de fragancias.
          </p>
          <Link to="/products">
            <button className="brand-btn" data-testid="continue-shopping-btn">
              Explorar Perfumes
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8 px-3 sm:px-4 md:px-12 max-w-7xl mx-auto min-h-screen bg-brand-bone overflow-x-hidden" data-testid="cart-page">
      <h1 className="font-display text-2xl md:text-4xl tracking-tight font-light mb-1 text-brand-black">
        Tu Carrito
      </h1>
      <p className="text-brand-black/50 text-sm mb-4 md:mb-8">
        {items.length} {items.length === 1 ? 'producto' : 'productos'}
      </p>

      <div className="grid lg:grid-cols-3 gap-4 lg:gap-12">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-3 md:space-y-6">
          {items.map((item) => (
            <div 
              key={`${item.product_id}-${item.size_ml || 100}`} 
              className="flex gap-3 md:gap-6 p-3 md:p-6 brand-card rounded-lg overflow-hidden"
              data-testid={`cart-item-${item.product_id}-${item.size_ml || 100}`}
            >
              {/* Product Image */}
              <div className="w-20 h-20 md:w-28 md:h-28 flex-shrink-0 bg-brand-black/5 overflow-hidden rounded">
                <img
                  src={item.main_image || 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=200'}
                  alt={item.product_name || 'Producto'}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <p className="text-[10px] md:text-xs text-brand-cobalt/60 uppercase tracking-wider truncate">
                      {item.inspired_by_brand}
                    </p>
                    <h3 className="text-xs md:text-base font-medium text-brand-black leading-tight line-clamp-2">
                      {item.product_code} - {item.product_name}
                    </h3>
                    {/* Size Badge */}
                    <span className="inline-block mt-1 px-2 py-0.5 bg-brand-cobalt/10 text-brand-cobalt text-[10px] md:text-xs font-medium rounded">
                      {(item.size_ml || 100) === 40 ? 35 : (item.size_ml || 100)}ml
                    </span>
                  </div>
                  <button
                    onClick={() => removeItem(item.product_id, item.size_ml || 100)}
                    className="text-brand-black/40 hover:text-brand-magenta p-1 flex-shrink-0 transition-colors"
                    data-testid={`remove-item-${item.product_id}-${item.size_ml || 100}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Price and Quantity */}
                <div className="flex items-center justify-between mt-2 md:mt-4 gap-2">
                  {/* Quantity Controls */}
                  <div className="flex items-center border border-brand-black/20 rounded flex-shrink-0">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.size_ml || 100)}
                      disabled={item.quantity <= 1}
                      className="p-1.5 md:p-2 text-brand-black hover:bg-brand-cobalt/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid={`decrease-qty-${item.product_id}-${item.size_ml || 100}`}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-6 md:w-10 text-center text-sm font-medium text-brand-black">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.size_ml || 100)}
                      className="p-1.5 md:p-2 text-brand-black hover:bg-brand-cobalt/5 transition-colors"
                      data-testid={`increase-qty-${item.product_id}-${item.size_ml || 100}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  {/* Price */}
                  <p className="font-semibold text-sm md:text-base text-brand-black flex-shrink-0">
                    {formatPrice(item.price * item.quantity)}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Promo de regalos (5x3): progreso y elección del 10ml */}
          <GiftPromoCard />

          {/* Continue Shopping */}
          <Link 
            to="/products" 
            className="inline-flex items-center gap-2 text-brand-black/60 hover:text-brand-cobalt transition-colors text-sm"
          >
            <ArrowRight size={14} className="rotate-180" />
            Continuar comprando
          </Link>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-brand-bone border border-brand-black/10 p-4 md:p-8 rounded-lg lg:sticky lg:top-32 overflow-hidden">
            <h2 className="font-display text-lg md:text-2xl tracking-tight mb-4 text-brand-black">
              Resumen
            </h2>

            {/* Coupon Input */}
            {!coupon ? (
              <form onSubmit={handleApplyCoupon} className="mb-4">
                <div className="flex gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Tag size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-cobalt/40" />
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="CUPÓN"
                      className="w-full pl-8 pr-2 py-2 bg-white border border-brand-black/20 focus:border-brand-cobalt focus:outline-none text-xs sm:text-sm uppercase text-brand-black placeholder:text-brand-black/30 rounded"
                      data-testid="coupon-input"
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={applyingCoupon || !couponCode.trim()}
                    className="px-3 sm:px-4 py-2 brand-btn-outline disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm rounded flex-shrink-0"
                    data-testid="apply-coupon-btn"
                  >
                    Aplicar
                  </button>
                </div>
              </form>
            ) : (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-400 font-medium">
                      {coupon.code}
                    </p>
                    <p className="text-xs text-green-400/70">
                      {coupon.discount_type === 'percentage' 
                        ? `${coupon.discount_value}% descuento`
                        : formatPrice(coupon.discount_value) + ' descuento'
                      }
                    </p>
                  </div>
                  <button
                    onClick={removeCoupon}
                    className="text-green-400 hover:text-green-300 p-1 transition-colors"
                    data-testid="remove-coupon-btn"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Price Breakdown */}
            <div className="space-y-2 border-t border-brand-black/10 pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-brand-black/60">Subtotal</span>
                <span className="text-brand-black">{formatPrice(totals.subtotal)}</span>
              </div>

              {/* Promo discount */}
              {totals.applied_promos && totals.applied_promos
                .filter((promo) => promo.discount_amount > 0)
                .map((promo, idx) => (
                <div key={idx} className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-400 font-medium flex items-center gap-1">
                      <Tag size={14} />
                      {promo.promo_name}
                    </span>
                    <span className="text-amber-400 font-bold">-{formatPrice(promo.discount_amount)}</span>
                  </div>
                  <p className="text-xs text-amber-400/70 mt-1">
                    {promo.promo_type === 'bundle_gift'
                      ? `Perfume de ${promo.pocket_size_ml === 40 ? 35 : promo.pocket_size_ml}ml de regalo`
                      : `Compra ${promo.buy_quantity}, paga ${promo.pay_quantity} • ${promo.items_free} gratis`}
                  </p>
                </div>
              ))}

              {totals.discount_amount > 0 && (
                <div className="flex justify-between text-sm text-green-400">
                  <span>Descuento cupón</span>
                  <span>-{formatPrice(totals.discount_amount)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-brand-black/60">Envío</span>
                <span className="text-brand-black/40 text-xs">En checkout</span>
              </div>

              <div className="flex justify-between pt-3 border-t border-brand-black/10">
                <span className="font-display text-base md:text-lg text-brand-black">Total</span>
                <span className="font-display text-lg md:text-xl text-brand-black font-bold">
                  {formatPrice(getFinalTotal())}
                </span>
              </div>
            </div>

            {/* Free Shipping Message */}
            {getFinalTotal() < FREE_SHIPPING_THRESHOLD && getFinalTotal() > 0 && (
              <div className="mt-3 p-2 bg-brand-cobalt/5 border border-brand-cobalt/20 text-center rounded">
                <p className="text-xs text-brand-cobalt">
                  +{formatPrice(FREE_SHIPPING_THRESHOLD - getFinalTotal())} para envío gratis
                </p>
              </div>
            )}

            {/* Checkout Button */}
            <button
              onClick={() => navigate('/checkout')}
              disabled={loading || giftPending}
              className="w-full mt-4 brand-btn disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-btn"
            >
              Ir al Checkout
            </button>
            {giftPending && (
              <p className="text-xs text-brand-magenta text-center mt-2" data-testid="gift-pending-msg">
                Elige tu perfume de 10ml de regalo para continuar
              </p>
            )}

            {/* Security Note */}
            <p className="text-[10px] md:text-xs text-brand-black/40 text-center mt-3">
              Pago seguro • Envío asegurado
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
